<template>
    <div class="expandable-settings-pane">
        <FontStyle
            v-if="isFontStyleVisible"
            :key="`${key}-font`"
            :computed-style="computedStyle"
            :bounding-client-rect="boundingClientRect"
            :controls="fontStyleControls"
            @onSettingsChange="onSettingsChange"
        />

        <ElementPosition
            v-if="editedEditable && !isCampaignAE && !isCampaignIndesign"
            :key="`${key}-element-position`"
            :computed-style="computedStyle"
            :bounding-client-rect="boundingClientRect"
            @onSettingsChange="onSettingsChange"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { EditableType } from "shared-utils/enums/editables";
import type { CSSSettings, EditableTypeType } from "shared-types";

import FontStyle from "@/components/Campaign/SettingsPane/FontStyle.vue";
import ElementPosition from "@/components/Campaign/SettingsPane/ElementPosition.vue";

import { useEditableMethods } from "@/composables/useEditableMethods";
import { useEditor } from "@/composables/useEditor";

import { EditorAction } from "@/store/modules/editor";
import { getBannerIdFromEditable } from "@/utils/getBannerIframeId";

type FontStyleControl =
    | "font"
    | "fontFamily"
    | "fontSize"
    | "letterSpacing"
    | "lineHeight"
    | "textAlignment"
    | "fontWeight"
    | "fontStyle"
    | "fontColor";

export default defineComponent({
    name: "EditableSettingsPane",
    components: {
        ElementPosition,
        FontStyle
    },

    setup(_, { emit }) {
        const { updateSettings } = useEditableMethods({ emit });

        const { editedEditable, editable, isCampaignAE, isCampaignPSD, isCampaignIndesign } = useEditor();

        return {
            updateSettings,

            editedEditable,
            editable,
            isCampaignAE,
            isCampaignPSD,
            isCampaignIndesign
        };
    },

    data() {
        return {
            computedStyle: {} as CSSStyleDeclaration,
            boundingClientRect: {} as DOMRect,
            unsubscribeActionListener: null as null | (() => void)
        };
    },

    computed: {
        isFontStyleVisible() {
            if (!this.editedEditable) {
                return false;
            }

            const textTypes = [EditableType.Text, EditableType.Textarea] as EditableTypeType[];

            return textTypes.includes(this.editable!.type);
        },

        selectedGroupValueIdsKey() {
            return this.$store.state.editor.selectedGroupValueIds.join("-");
        },

        key() {
            return `${this.selectedGroupValueIdsKey}-${this.editedEditable?.editable._id}-${this.editedEditable?.masterTemplateId}`;
        },

        fontStyleControls(): FontStyleControl[] {
            if (this.isCampaignAE) {
                return ["font", "fontSize"];
            }

            if (this.isCampaignIndesign) {
                return ["fontSize"];
            }

            const controls: FontStyleControl[] = [
                "fontFamily",
                "fontSize",
                "letterSpacing",
                "lineHeight",
                "fontWeight",
                "fontStyle",
                "fontColor"
            ];

            if (this.isCampaignPSD) {
                controls.splice(4, 0, "textAlignment");
            }

            return controls;
        }
    },

    watch: {
        editedEditable: {
            immediate: true,
            handler() {
                this.initSettings();
            }
        }
    },

    created() {
        this.unsubscribeActionListener = this.$store.subscribeAction({
            after: action => {
                if (this.editedEditable && action.type === EditorAction.ClearLocalChanges) {
                    this.initSettings();
                }
            }
        });
    },

    beforeDestory() {
        this.unsubscribeActionListener?.();
    },

    methods: {
        onSettingsChange(settings: CSSSettings | null) {
            this.updateSettings({
                editable: this.editedEditable!.editable,
                // the backend throw an error if we send null, so we need to send an empty object instead
                // (always beenen like this, but we should consider changing it in the future)
                settings: settings ?? {},
                groupsAffectingEditable: this.editedEditable!.groupsAffectingEditable,
                templateId: this.editedEditable!.masterTemplateId
            });
        },

        initSettings() {
            if (!this.editedEditable?.editable) {
                this.computedStyle = {} as CSSStyleDeclaration;

                return;
            }

            if (
                !this.editedEditable.masterTemplateId ||
                this.isCampaignPSD ||
                this.isCampaignAE ||
                this.isCampaignIndesign
            ) {
                this.resetStyling();

                return;
            }

            try {
                const selector = getBannerIdFromEditable(this.editedEditable, "edit");
                const iframeEl = document.getElementById(selector) as HTMLIFrameElement;

                if (!iframeEl?.contentWindow) {
                    throw new Error("Iframe content window is not accessible.");
                }

                const iframeDocument = iframeEl.contentWindow.document;
                const editableId = `#${this.editable?.name}`;

                const editableEl = iframeDocument.querySelector(editableId)!;

                if (!editableEl) {
                    throw new Error("Editable element not found in iframe.");
                }

                this.boundingClientRect = editableEl.getBoundingClientRect();
                this.computedStyle = iframeEl.contentWindow.getComputedStyle(editableEl) || {};
            } catch (error) {
                console.error("Error initializing settings:", error);
                this.resetStyling();
            }
        },

        resetStyling() {
            this.computedStyle = {} as CSSStyleDeclaration;
            this.boundingClientRect = {} as DOMRect;
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.expandable-settings-pane {
    height: 100%;
    overflow: hidden auto;

    svg {
        fill: $cmp-dark-font-color;
    }

    .ivu-btn {
        background-color: $cmp-dark-input-bg-color;
        border-color: #dcdee2;
        margin: 2px;
    }

    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input::-webkit-input-placeholder {
        /* Chrome/Opera/Safari */
        color: $grey5;
    }
    input::-moz-placeholder {
        /* Firefox 19+ */
        color: $grey5;
    }
    input:-ms-input-placeholder {
        /* IE 10+ */
        color: $grey5;
    }
    input:-moz-placeholder {
        /* Firefox 18- */
        color: $grey5;
    }

    /* Firefox */
    input[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
}

.editable-settings {
    &-item {
        width: 40px;
        height: 33px;
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__input {
        flex: 1 1 43px;
        width: 43px;

        &-label {
            padding-right: $spacing-small;
            width: 20px;
            flex: 0 0 20px;
            &--second {
                margin-left: 10px;
            }
        }

        .ivu-input-group-append {
            font-size: 10px;
            background-color: $cmp-dark-input-bg-color;
            border-color: $darktheme20;
            padding: 0;
            line-height: normal;
            height: 24px;
        }

        &:not(&--with-suffix) {
            .ivu-input-group-append {
                width: 21px !important;
            }
        }

        &--with-suffix {
            .ivu-input {
                padding-right: 2px;
            }

            .ivu-input-group-append {
                padding-left: 2px;
                padding-right: 7px;
            }
        }

        .ivu-input {
            border-right: 0;
            text-align: right;
            height: 24px;
        }

        .ivu-input:focus {
            box-shadow: none;
        }

        &-wrapper {
            flex: 1 1 100%;
            display: flex;
            justify-content: flex-start;
            align-items: center;
        }
    }
}
</style>
